import React from 'react';
import Paper from '@mui/material/Paper';
import Stack from '@mui/material/Stack';
import Box from '@mui/material/Box';
import Typography from '@mui/material/Typography';
import Avatar from '@mui/material/Avatar';
import Chip from '@mui/material/Chip';
import Grid from '@mui/material/Grid';

/**
 * TrangXemHienDai — các khối dựng TRANG XEM MỘT BẢN GHI theo bố cục hiện đại, cùng ngôn ngữ với trang xem
 * quảng cáo `ad_campaign` của gói marketing. Dùng CHUNG cho:
 *   - ViewBase  (bộ cũ: /view/<code>/<id>, hộp thoại ObjectView)
 *   - DataView  (trang xem mặc định của DataListManager)
 * để hai trang xem trông như một.
 *
 * Bố cục (một cột, từ trên xuống):
 *   DaiNhanDang  → ảnh/chữ cái đầu, tên, chip (màn hình, trạng thái, mã), ngày tạo/cập nhật
 *   OSoLieu × ≤6 → các số chính, vạch màu trên đầu (MAU_O_SO xoay vòng)
 *   TheXem       → "Hình ảnh", "Thông tin" (lưới OThongTin), thẻ TAB, "Vị trí", "Ý kiến"…
 * Mọi thẻ là Paper viền mảnh bo 8px; tiêu đề thẻ chữ đậm màu chủ đạo, có dòng giải thích nhỏ khi cần.
 */

/** Màu vạch trên đầu các ô số liệu chính (xoay vòng) — cùng bảng màu các ô của trang quảng cáo. */
export const MAU_O_SO = ['#1976d2', '#2e7d32', '#6a1b9a', '#ef6c00', '#00838f', '#c62828'];

/** Số ô số liệu chính tối đa trên một trang xem. */
export const SO_O_SO_TOI_DA = 6;

/** Thanh tab trong thẻ: chữ thường, đậm, viền dưới mảnh. */
export const sxThanhTabXem = { borderBottom: '1px solid', borderColor: 'divider', px: 1, minHeight: 44, '& .MuiTab-root': { textTransform: 'none', fontWeight: 600, minHeight: 44 } };

/**
 * Nội dung tab nằm TRONG một thẻ đã có viền: bảng InputBase bên trong bỏ khung viền/bo góc riêng của nó
 * (khỏi viền đôi), nền danh sách trong suốt để lộ nền trắng của thẻ.
 */
export const sxNoiDungTab = { '& .MuiTableContainer-root': { border: 'none', borderRadius: 0 }, '& .flex-inputbase': { backgroundColor: 'transparent !important' } };

/** Một thẻ của trang xem. */
export function TheXem({ tieuDe, phai, moTa, children, sx, className }) {
  return (
    <Paper variant="outlined" className={className || 'trang-xem-the'} sx={{ p: { xs: 1.5, md: 2 }, borderRadius: 2, mb: 2, ...(sx || {}) }}>
      {(tieuDe || phai) && (
        <Stack direction="row" alignItems="center" justifyContent="space-between" gap={1} flexWrap="wrap" sx={{ mb: moTa ? 0.25 : 1.5 }}>
          <Typography variant="subtitle2" fontWeight={700} color="primary.main" component="div">{tieuDe}</Typography>
          {phai || null}
        </Stack>
      )}
      {moTa ? <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mb: 1.5 }}>{moTa}</Typography> : null}
      {children}
    </Paper>
  );
}

/**
 * Dải nhận dạng trên cùng.
 * @param anh      node ảnh đại diện (vd <SecureImage/>) — không có thì hiện `chuCai`
 * @param chuCai   chữ cái hiện trong ô đại diện khi không có ảnh
 * @param tieuDe   tên bản ghi
 * @param dongPhu  mảng dòng phụ dưới tên
 * @param chips    mảng { label, sx?, variant?, color? }
 * @param mauNhan  màu vạch trái + nền ô đại diện (mặc định màu chủ đạo; trang xem truyền màu trạng thái)
 * @param benPhai  khối bên phải (ngày tạo / cập nhật, nút…)
 */
export function DaiNhanDang({ anh, chuCai, tieuDe, dongPhu, chips, mauNhan, benPhai }) {
  const mau = mauNhan || 'primary.main';
  return (
    <Paper variant="outlined" className="trang-xem-nhan-dang" sx={{ p: 2, borderRadius: 2, mb: 2, borderLeft: '4px solid', borderLeftColor: mau }}>
      <Stack direction="row" gap={2} alignItems="center" flexWrap="wrap">
        <Avatar variant="rounded" sx={{ width: 52, height: 52, bgcolor: mau, fontSize: 22, fontWeight: 700, '& img': { width: '100%', height: '100%', objectFit: 'cover' } }}>
          {anh || (String(chuCai || '').trim().charAt(0).toUpperCase() || '#')}
        </Avatar>
        <Box sx={{ flexGrow: 1, minWidth: 200 }}>
          <Typography component="div" variant="h6" fontWeight={700} sx={{ wordBreak: 'break-word', lineHeight: 1.3 }}>{tieuDe || '—'}</Typography>
          {(dongPhu || []).filter(Boolean).map((t, i) => (
            <Typography key={i} component="div" variant="body2" color="text.secondary" fontWeight={500}>{t}</Typography>
          ))}
          {(chips || []).length > 0 && (
            <Stack direction="row" gap={1} alignItems="center" flexWrap="wrap" sx={{ mt: 0.75 }}>
              {chips.map((c, i) => <Chip key={i} size="small" label={c.label} variant={c.variant} color={c.color} sx={c.sx} />)}
            </Stack>
          )}
        </Box>
        {benPhai ? <Box sx={{ textAlign: { xs: 'left', md: 'right' } }}>{benPhai}</Box> : null}
      </Stack>
    </Paper>
  );
}

/** Dòng "Tạo lúc … · người tạo" / "Cập nhật …" cho khối bên phải của dải nhận dạng. */
export function DongNgayTao({ nhan, ngay, nguoi, dinhDang }) {
  if (!ngay) return null;
  return (
    <Typography variant="caption" color="text.secondary" sx={{ display: 'block' }}>
      {nhan} {dinhDang ? dinhDang(ngay) : String(ngay)}{nguoi ? ' · ' + nguoi : ''}
    </Typography>
  );
}

/** Một ô số liệu chính: nhãn nhỏ, số lớn, vạch màu trên đầu. Đặt trong <Grid container>. */
export function OSoLieu({ nhan, giaTri, phu, mau }) {
  return (
    <Grid size={{ xs: 6, sm: 4, md: 2 }} sx={{ display: 'flex' }}>
      <Paper variant="outlined" sx={{ p: 1.5, borderRadius: 2, flexGrow: 1, minWidth: 0, borderTop: '3px solid', borderTopColor: mau || 'divider' }}>
        <Typography variant="caption" color="text.secondary" sx={{ display: 'block' }} noWrap>{nhan}</Typography>
        <Typography component="div" variant="h6" fontWeight={700} sx={{ lineHeight: 1.3 }} noWrap>{giaTri == null || giaTri === '' ? '—' : giaTri}</Typography>
        {phu ? <Typography variant="caption" color="text.secondary" sx={{ display: 'block' }} noWrap>{phu}</Typography> : null}
      </Paper>
    </Grid>
  );
}

/** Hàng ô số liệu chính, có nhãn nhỏ "SỐ LIỆU CHÍNH" phía trên. */
export function HangSoLieu({ nhan = 'Số liệu chính', children }) {
  const ds = React.Children.toArray(children).filter(Boolean);
  if (!ds.length) return null;
  return (
    <>
      <Typography variant="overline" color="text.secondary" sx={{ display: 'block', mb: 0.5 }}>{nhan}</Typography>
      <Grid container spacing={1.5} sx={{ mb: 2 }}>{ds}</Grid>
    </>
  );
}

/** Một ô của thẻ "Thông tin": nhãn nhỏ xám ở trên, giá trị đậm ở dưới; `rong` = chiếm trọn hàng. */
export function OThongTin({ nhan, children, rong, size, styleNhan, styleGiaTri }) {
  return (
    <Grid size={size || (rong ? { xs: 12 } : { xs: 12, sm: 6, md: 4 })}>
      <Box sx={{ display: 'flex', flexDirection: 'column', minWidth: 0 }}>
        {typeof nhan === 'string'
          ? <Typography variant="caption" color="text.secondary" sx={{ display: 'block' }} style={styleNhan}>{nhan}</Typography>
          : nhan}
        <Typography component="div" variant="body2" fontWeight={600}
          sx={{ wordBreak: 'break-word', '& .MuiTypography-body1': { fontSize: 'inherit', fontWeight: 'inherit' } }} style={styleGiaTri}>
          {children == null || children === '' ? '—' : children}
        </Typography>
      </Box>
    </Grid>
  );
}

/** Lưới các ô thông tin. */
export function LuoiThongTin({ children }) {
  return <Grid container rowSpacing={2} columnSpacing={3}>{children}</Grid>;
}

/** Field mã của bản ghi để hiện thành chip "Mã: …" (ma_*, so_ct, code) — không lấy field tiêu đề. */
export function laTruongMa(ten) {
  return /^(ma_|so_ct$|code$)/.test(String(ten || ''));
}
